import * as ExcelJS from 'exceljs';
import i18n from '../locales/i18n';

interface TradeData {
  TradeNumber: string;
  CounterpartyName: string;
  MaturityDate: string;
  Direction: string;
  Currency1: string;
  QuantityCurrency1: number;
  Price: number;
  Currency2: string;
  SettlementCurrency: string;
  SettlementType?: string;
  FixingPrice?: number;
  FixingRate?: number;
  DisplayFixingPrice?: number;
  SettlementPrice: number;
  WePayCurrency: string;
  WePayAmount: number;
  WeReceiveCurrency: string;
  WeReceiveAmount: number;
  extractedFields?: string[];
}

export const exportTradeToExcel = async (ourTrade: TradeData, cptyTrade: TradeData) => {
  const t = i18n.t.bind(i18n);
  const isPhysicalDelivery = ourTrade.SettlementType !== 'Compensación';

  // Create a new workbook and worksheet
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Settlement Calculation');

  // Row 1: Title (merged)
  const excelTitle = isPhysicalDelivery
    ? t('forwardMaturity.excel.titlePhysicalDelivery')
    : t('forwardMaturity.excel.title');
  worksheet.getCell('A1').value = excelTitle;
  worksheet.mergeCells('A1:D1');
  worksheet.getCell('F1').value = excelTitle;
  worksheet.mergeCells('F1:I1');

  // Row 2: Trade Number + Settlement Type
  worksheet.getCell('A2').value = t('forwardMaturity.columns.tradeNumber') + ':';
  worksheet.getCell('B2').value = ourTrade.TradeNumber;
  worksheet.getCell('C2').value = ourTrade.SettlementType || '';

  // Row 3: Blank

  // Row 4: "Our Calculations" / "Counterparty Calculations"
  worksheet.getCell('A4').value = t('forwardMaturity.ourCalculations');
  worksheet.getCell('F4').value = t('forwardMaturity.counterpartyCalculations');

  // Row 5: Blank

  // Row 6: Column headers
  worksheet.getRow(6).values = [
    t('forwardMaturity.excel.field'),
    t('forwardMaturity.excel.value'),
    t('forwardMaturity.excel.currency'),
    t('forwardMaturity.excel.formula'),
    '',
    t('forwardMaturity.excel.field'),
    t('forwardMaturity.excel.value'),
    t('forwardMaturity.excel.currency'),
    ''
  ];

  // Helper: check if a counterparty field was actually extracted from email
  const wasExtracted = (field: string): boolean => {
    if (!cptyTrade.extractedFields) return true;
    return cptyTrade.extractedFields.includes(field);
  };

  // Row 7: Counterparty
  worksheet.getRow(7).values = [
    t('forwardMaturity.columns.counterparty'),
    ourTrade.CounterpartyName,
    '', '', '',
    t('forwardMaturity.columns.counterparty'),
    cptyTrade.CounterpartyName
  ];

  // Row 8: Maturity Date
  worksheet.getRow(8).values = [
    t('forwardMaturity.columns.maturityDate'),
    ourTrade.MaturityDate,
    '', '', '',
    t('forwardMaturity.columns.maturityDate'),
    cptyTrade.MaturityDate
  ];

  // Row 9: Direction
  worksheet.getRow(9).values = [
    t('forwardMaturity.columns.direction'),
    ourTrade.Direction,
    '', '', '',
    t('forwardMaturity.columns.direction'),
    wasExtracted('Direction') ? cptyTrade.Direction : ''
  ];

  // Row 10: Currency 1
  worksheet.getRow(10).values = [
    t('forwardMaturity.columns.currency1'),
    '',
    ourTrade.Currency1,
    '', '',
    t('forwardMaturity.columns.currency1'),
    '',
    wasExtracted('Currency1') ? cptyTrade.Currency1 : ''
  ];

  // Row 11: Traded Amount
  worksheet.getRow(11).values = [
    t('forwardMaturity.columns.tradedAmount'),
    ourTrade.QuantityCurrency1,
    ourTrade.Currency1,
    '', '',
    t('forwardMaturity.columns.tradedAmount'),
    wasExtracted('QuantityCurrency1') ? cptyTrade.QuantityCurrency1 : '',
    wasExtracted('QuantityCurrency1') ? cptyTrade.Currency1 : ''
  ];

  // Row 12: Price
  worksheet.getRow(12).values = [
    t('forwardMaturity.columns.price'),
    ourTrade.Price,
    '', '', '',
    t('forwardMaturity.columns.price'),
    wasExtracted('Price') ? cptyTrade.Price : ''
  ];

  // Row 13: Currency 2
  worksheet.getRow(13).values = [
    t('forwardMaturity.columns.currency2'),
    '',
    ourTrade.Currency2,
    '', '',
    t('forwardMaturity.columns.currency2'),
    '',
    wasExtracted('Currency2') ? cptyTrade.Currency2 : ''
  ];

  if (isPhysicalDelivery) {
    // --- Physical delivery: no fixing/settlement rows, show N/A ---

    // Row 14: Settlement Currency → N/A
    worksheet.getRow(14).values = [
      t('forwardMaturity.excel.settlementCurrency'),
      'N/A', '', '', '',
      t('forwardMaturity.excel.settlementCurrency'),
      'N/A'
    ];

    // Row 15: Fixing Price → N/A
    worksheet.getRow(15).values = [
      t('forwardMaturity.columns.fixingPrice'),
      'N/A', '', '', '',
      t('forwardMaturity.columns.fixingPrice'),
      'N/A'
    ];

    // Row 16: Settlement Price → N/A
    worksheet.getRow(16).values = [
      t('forwardMaturity.columns.settlementExchangeRate'),
      'N/A', '', '', '',
      t('forwardMaturity.columns.settlementExchangeRate'),
      'N/A'
    ];

    // Row 17: Blank

    // Row 18: "Calculations"
    worksheet.getRow(18).values = [
      t('forwardMaturity.excel.calculations'),
      '', '', '', '',
      t('forwardMaturity.excel.calculations')
    ];

    // Row 19: Blank

    // --- Our side: physical delivery formulas ---
    // Buy: WePay = Qty1 × Price (Currency2), WeReceive = Qty1 (Currency1)
    // Sell: WePay = Qty1 (Currency1), WeReceive = Qty1 × Price (Currency2)

    // Row 20: We Pay
    worksheet.getCell('A20').value = t('forwardMaturity.columns.pagamos');
    worksheet.getCell('B20').value = {
      formula: 'IF(B9="Buy", B11*B12, B11)'
    };
    worksheet.getCell('B20').numFmt = '#,##0.00';
    worksheet.getCell('C20').value = {
      formula: `IF(B9="Buy", "${ourTrade.Currency2}", "${ourTrade.Currency1}")`
    };
    worksheet.getCell('D20').value = 'Buy → Qty1 × Price (Ccy2); Sell → Qty1 (Ccy1)';

    // Row 21: We Receive
    worksheet.getCell('A21').value = t('forwardMaturity.columns.recibimos');
    worksheet.getCell('B21').value = {
      formula: 'IF(B9="Buy", B11, B11*B12)'
    };
    worksheet.getCell('B21').numFmt = '#,##0.00';
    worksheet.getCell('C21').value = {
      formula: `IF(B9="Buy", "${ourTrade.Currency1}", "${ourTrade.Currency2}")`
    };
    worksheet.getCell('D21').value = 'Buy → Qty1 (Ccy1); Sell → Qty1 × Price (Ccy2)';

  } else {
    // --- NDF (Compensación): fixing-based settlement ---

    // Row 14: Settlement Currency
    worksheet.getRow(14).values = [
      t('forwardMaturity.excel.settlementCurrency'),
      '',
      ourTrade.SettlementCurrency,
      '', '',
      t('forwardMaturity.excel.settlementCurrency'),
      '',
      cptyTrade.SettlementCurrency
    ];

    // Row 15: Fixing Price
    worksheet.getRow(15).values = [
      t('forwardMaturity.columns.fixingPrice'),
      ourTrade.DisplayFixingPrice || ourTrade.FixingRate || ourTrade.FixingPrice || 0,
      '', '', '',
      t('forwardMaturity.columns.fixingPrice'),
      wasExtracted('FixingPrice') ? (cptyTrade.FixingPrice || 0) : ''
    ];

    // Row 16: Settlement Price
    worksheet.getRow(16).values = [
      t('forwardMaturity.columns.settlementExchangeRate'),
      ourTrade.SettlementPrice,
      '', '', '',
      t('forwardMaturity.columns.settlementExchangeRate'),
      wasExtracted('ConversionRate') ? (cptyTrade.SettlementPrice || 0) : ''
    ];

    // Row 17: Blank

    // Row 18: "Calculations"
    worksheet.getRow(18).values = [
      t('forwardMaturity.excel.calculations'),
      '', '', '', '',
      t('forwardMaturity.excel.calculations')
    ];

    // Row 19: Blank

    // --- Our side: NDF formulas ---

    // Row 20: We Pay
    worksheet.getCell('A20').value = t('forwardMaturity.columns.pagamos');
    worksheet.getCell('B20').value = {
      formula: 'IF(B9="Buy", IF((B15-B12)/B16<0, ABS((B11*(B15-B12)/B16)), 0), IF((B12-B15)/B16<0, ABS((B11*(B12-B15)/B16)), 0))'
    };
    worksheet.getCell('C20').value = ourTrade.SettlementCurrency;
    worksheet.getCell('D20').value = t('forwardMaturity.excel.wePayFormula');

    // Row 21: We Receive
    worksheet.getCell('A21').value = t('forwardMaturity.columns.recibimos');
    worksheet.getCell('B21').value = {
      formula: 'IF(B9="Buy", IF((B15-B12)/B16>=0, (B11*(B15-B12)/B16), 0), IF((B12-B15)/B16>=0, (B11*(B12-B15)/B16), 0))'
    };
    worksheet.getCell('C21').value = ourTrade.SettlementCurrency;
    worksheet.getCell('D21').value = t('forwardMaturity.excel.weReceiveFormula');
  }

  // --- Counterparty side: hardcoded settlement amounts with conditional formatting ---
  // Green when counterparty amount matches our formula result, red otherwise.
  // Conditional formatting is live — editing our-side inputs recalculates the comparison.

  // Row 20: Counterparty We Pay
  worksheet.getCell('F20').value = t('forwardMaturity.columns.pagamos');
  worksheet.getCell('G20').value = cptyTrade.WePayAmount || 0;
  worksheet.getCell('G20').numFmt = '#,##0.00';
  worksheet.getCell('H20').value = cptyTrade.WePayCurrency || cptyTrade.SettlementCurrency;

  // Row 21: Counterparty We Receive
  worksheet.getCell('F21').value = t('forwardMaturity.columns.recibimos');
  worksheet.getCell('G21').value = cptyTrade.WeReceiveAmount || 0;
  worksheet.getCell('G21').numFmt = '#,##0.00';
  worksheet.getCell('H21').value = cptyTrade.WeReceiveCurrency || cptyTrade.SettlementCurrency;

  // Conditional formatting: compare counterparty amounts (G20:G21) against our formulas (B20:B21)
  // Rule 1 (priority 1): match → green fill, white font
  // Rule 2 (priority 2): mismatch → red fill, white font
  worksheet.addConditionalFormatting({
    ref: 'G20:G21',
    rules: [
      {
        type: 'expression',
        priority: 1,
        formulae: ['ABS(G20-B20)<0.01'],
        style: {
          fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FF28A745' } },
          font: { color: { argb: 'FFFFFFFF' } }
        }
      },
      {
        type: 'expression',
        priority: 2,
        formulae: ['ABS(G20-B20)>=0.01'],
        style: {
          fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFDC3545' } },
          font: { color: { argb: 'FFFFFFFF' } }
        }
      }
    ]
  });

  // Set column widths
  worksheet.getColumn(1).width = 20; // Field name
  worksheet.getColumn(2).width = 15; // Value
  worksheet.getColumn(3).width = 8;  // Currency
  worksheet.getColumn(4).width = 40; // Formula description
  worksheet.getColumn(5).width = 3;  // Spacer
  worksheet.getColumn(6).width = 20; // Field name
  worksheet.getColumn(7).width = 15; // Value
  worksheet.getColumn(8).width = 8;  // Currency
  worksheet.getColumn(9).width = 40; // Notes

  // Generate file name
  const fileName = `Trade_${ourTrade.TradeNumber}_Settlement.xlsx`;

  // Write file to browser (download)
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
};
